/**
 * Formatting Utilities
 *
 * Shared utilities for formatting money, dates, and other values.
 */

import type { Money } from '../types/common';

// ============================================================================
// Currency Configuration
// ============================================================================

interface CurrencyConfig {
  locale: string;
  decimalPlaces: number;
}

const CURRENCY_CONFIG: Record<string, CurrencyConfig> = {
  INR: { locale: 'en-IN', decimalPlaces: 2 },
  USD: { locale: 'en-US', decimalPlaces: 2 },
  EUR: { locale: 'de-DE', decimalPlaces: 2 },
  GBP: { locale: 'en-GB', decimalPlaces: 2 },
  JPY: { locale: 'ja-JP', decimalPlaces: 0 },
  CNY: { locale: 'zh-CN', decimalPlaces: 2 },
  AUD: { locale: 'en-AU', decimalPlaces: 2 },
  CAD: { locale: 'en-CA', decimalPlaces: 2 },
  CHF: { locale: 'de-CH', decimalPlaces: 2 },
  SGD: { locale: 'en-SG', decimalPlaces: 2 },
};

const DEFAULT_CURRENCY_CONFIG: CurrencyConfig = {
  locale: 'en-US',
  decimalPlaces: 2,
};

/**
 * Get currency configuration for a currency code.
 */
export function getCurrencyConfig(currency: string): CurrencyConfig {
  return CURRENCY_CONFIG[currency] ?? DEFAULT_CURRENCY_CONFIG;
}

// ============================================================================
// Money Formatting
// ============================================================================

export interface FormatMoneyOptions {
  /** Override the default locale for this currency */
  locale?: string;
  /** Show currency symbol (default: true) */
  showSymbol?: boolean;
  /** Compact notation for large numbers (e.g., 1.2M) */
  compact?: boolean;
  /** Show sign for positive numbers */
  signDisplay?: 'auto' | 'always' | 'exceptZero' | 'never';
}

/**
 * Format a Money object for display.
 *
 * @param money - The money object to format
 * @param options - Formatting options
 * @returns Formatted string
 *
 * @example
 * formatMoney({ amount: 100000, currency: 'INR' })
 * // Returns "₹1,000.00"
 *
 * formatMoney({ amount: 100000, currency: 'INR' }, { compact: true })
 * // Returns "₹1K"
 */
export function formatMoney(money: Money, options: FormatMoneyOptions = {}): string {
  const config = getCurrencyConfig(money.currency);
  const locale = options.locale ?? config.locale;
  const divisor = Math.pow(10, config.decimalPlaces);

  // Handle string amounts from backend (parse to number)
  const amount = typeof money.amount === 'string' ? parseFloat(money.amount) : money.amount;

  // Handle invalid amounts
  if (isNaN(amount)) {
    return new Intl.NumberFormat(locale, {
      style: options.showSymbol === false ? 'decimal' : 'currency',
      currency: money.currency,
    }).format(0);
  }

  const formatOptions: Intl.NumberFormatOptions = {
    style: options.showSymbol === false ? 'decimal' : 'currency',
    currency: money.currency,
    minimumFractionDigits: config.decimalPlaces,
    maximumFractionDigits: config.decimalPlaces,
    signDisplay: options.signDisplay,
  };

  if (options.compact) {
    formatOptions.notation = 'compact';
    formatOptions.compactDisplay = 'short';
  }

  return new Intl.NumberFormat(locale, formatOptions).format(amount / divisor);
}

/**
 * Format a raw amount with a currency.
 */
export function formatAmount(
  amount: number,
  currency: string,
  options: FormatMoneyOptions = {}
): string {
  return formatMoney({ amount, currency }, options);
}

/**
 * Alias for formatAmount for backwards compatibility.
 */
export const formatCurrency = formatAmount;

/**
 * Format customer PO amounts (amounts are in paisa).
 * formatMoney already handles division by 100, so we just pass through.
 */
export function formatCustomerPOAmount(
  amount: number,
  currency: string = 'INR'
): string {
  return formatAmount(amount, currency);
}

// ============================================================================
// Date Formatting
// ============================================================================

/**
 * Parse a date-only string (YYYY-MM-DD) as local midnight, not UTC.
 * `new Date('2025-05-31')` is parsed as UTC midnight per JS spec,
 * which shifts the date backward in timezones ahead of UTC (e.g. IST).
 * Appending T00:00:00 forces local timezone interpretation.
 */
export function parseLocalDate(s: string): Date {
  return new Date(s.includes('T') ? s : `${s}T00:00:00`)
}

/**
 * Format a Date object to YYYY-MM-DD using local timezone (not UTC).
 * Safe replacement for `date.toISOString().split('T')[0]` which uses UTC.
 */
export function toLocalDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export interface FormatDateOptions {
  locale?: string;
  style?: 'full' | 'long' | 'medium' | 'short';
}

/**
 * Format a date string for display.
 */
export function formatDate(
  dateString: string,
  options: FormatDateOptions = {}
): string {
  const date = new Date(dateString.includes('T') ? dateString : `${dateString}T00:00:00`);
  const locale = options.locale ?? 'en-US';
  const style = options.style ?? 'medium';

  return date.toLocaleDateString(locale, { dateStyle: style });
}

/**
 * Format a date range for display.
 */
export function formatDateRange(
  startDate: string,
  endDate: string,
  options: FormatDateOptions = {}
): string {
  const start = formatDate(startDate, options);
  const end = formatDate(endDate, options);
  return `${start} - ${end}`;
}

// ============================================================================
// Number Formatting
// ============================================================================

/**
 * Format a percentage value.
 */
export function formatPercent(
  value: number,
  decimalPlaces: number = 2,
  locale: string = 'en-US'
): string {
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(value);
}

/**
 * Format a number with thousands separators.
 */
export function formatNumber(
  value: number,
  locale: string = 'en-US',
  decimalPlaces?: number
): string {
  const options: Intl.NumberFormatOptions = {};
  if (decimalPlaces !== undefined) {
    options.minimumFractionDigits = decimalPlaces;
    options.maximumFractionDigits = decimalPlaces;
  }
  return new Intl.NumberFormat(locale, options).format(value);
}

// ============================================================================
// Money Utilities
// ============================================================================

/**
 * Parse a money amount to a number, handling both string and number inputs.
 * Useful for performing arithmetic on money amounts from the backend.
 */
export function parseMoneyAmount(amount: number | string | undefined): number {
  if (amount === undefined || amount === null) {return 0;}
  if (typeof amount === 'number') {return amount;}
  const parsed = parseFloat(amount);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Get the divisor for converting between smallest unit and decimal for a currency.
 * e.g., INR/USD/GBP = 100 (2 decimal places), JPY = 1 (0 decimal places).
 */
export function getCurrencyDivisor(currency: string): number {
  return Math.pow(10, getCurrencyConfig(currency).decimalPlaces);
}

/**
 * Return the first value that is non-undefined and non-blank after trimming.
 * Useful where empty-string-to-fallback semantics are intended (which `??`
 * does NOT give — it only falls back on null/undefined). Returns undefined
 * when every candidate is blank.
 */
export function firstNonBlank(...values: Array<string | null | undefined>): string | undefined {
  for (const v of values) {
    const trimmed = v?.trim();
    if (trimmed !== undefined && trimmed !== '') { return trimmed; }
  }
  return undefined;
}

/**
 * Convert a decimal amount to the smallest currency unit.
 * e.g., 35.50 INR -> 3550, 100 JPY -> 100
 */
export function toSmallestUnit(amount: number, currency: string): number {
  return Math.round(amount * getCurrencyDivisor(currency));
}

/**
 * Convert from smallest currency unit to decimal.
 * e.g., 3550 INR -> 35.50, 100 JPY -> 100
 */
export function fromSmallestUnit(amount: number, currency: string): number {
  return amount / getCurrencyDivisor(currency);
}

// ============================================================================
// Geography/Location Formatting
// ============================================================================

/**
 * Get country flag emoji from country code.
 * Uses Unicode regional indicator symbols.
 */
export function getCountryFlag(countryCode: string): string {
  if (countryCode?.length !== 2) {return '';}

  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));

  return String.fromCodePoint(...codePoints);
}

/**
 * Common country flags for fallback
 */
const COUNTRY_FLAGS: Record<string, string> = {
  'IN': '🇮🇳',
  'US': '🇺🇸',
  'GB': '🇬🇧',
  'CA': '🇨🇦',
  'AU': '🇦🇺',
  'SG': '🇸🇬',
  'AE': '🇦🇪',
  'DE': '🇩🇪',
  'FR': '🇫🇷',
  'JP': '🇯🇵',
  'CN': '🇨🇳',
  'BR': '🇧🇷',
  'MX': '🇲🇽',
  'IT': '🇮🇹',
  'ES': '🇪🇸',
  'NL': '🇳🇱',
  'CH': '🇨🇭',
  'SE': '🇸🇪',
  'NO': '🇳🇴',
  'DK': '🇩🇰',
  'FI': '🇫🇮',
  'PL': '🇵🇱',
  'PT': '🇵🇹',
  'GR': '🇬🇷',
  'TR': '🇹🇷',
  'RU': '🇷🇺',
  'KR': '🇰🇷',
  'TH': '🇹🇭',
  'MY': '🇲🇾',
  'ID': '🇮🇩',
  'PH': '🇵🇭',
  'VN': '🇻🇳',
  'NZ': '🇳🇿',
  'ZA': '🇿🇦',
  'EG': '🇪🇬',
  'SA': '🇸🇦',
  'IL': '🇮🇱',
  'AR': '🇦🇷',
  'CL': '🇨🇱',
  'CO': '🇨🇴',
  'PE': '🇵🇪',
};

/**
 * Get country flag emoji with fallback
 */
export function getCountryFlagSafe(countryCode: string): string {
  if (!countryCode) {return '';}
  const code = countryCode.toUpperCase();
  return COUNTRY_FLAGS[code] ?? getCountryFlag(code);
}
